/** WBAgentBillingState is the billing snapshot the store holds. */
export type WBAgentBillingState =
  | {status: 'unenforced'}
  | {status: 'resolving'}
  | {status: 'error'}
  | WBAgentBillingReadyState;

/**
 * WBAgentBillingReadyState is the resolved billing data, present only once
 * billing has settled into a ready status.
 */
export type WBAgentBillingReadyState = {
  billingPeriodEndAt: number | null;
  billingPeriodStartAt: number | null;
  isPaygoEnabled: boolean;
  isQuotaEnforced: boolean;
  isTrial: boolean;
  isTrialExpired: boolean;
  quotaLimit: number;
  quotaSafetyLimit: number;
  quotaUsed: number;
  status: 'ready';
};

/**
 * WBAgentBillingAccess is the gating verdict consumers act on. Guards read only
 * `accessible`; the banner switches on the `reason` block, which names the
 * single most actionable cause and carries the sub-path the copy needs. See
 * `selectWBAgentBillingAccess` for ordering.
 */
export type WBAgentBillingAccess =
  | {accessible: true}
  | ({accessible: false} & WBAgentBillingBlockedReason);

/**
 * WBAgentBillingBlockedReason is the reason access is denied, discriminated by
 * `reason`.
 */
export type WBAgentBillingBlockedReason =
  | {reason: 'resolving'}
  | {reason: 'error'}
  | {reason: 'quota'}
  | {reason: 'cap'}
  | {reason: 'upgrade'}
  | {reason: 'trial'; expired: boolean};

export const INITIAL_BILLING_STATE: WBAgentBillingState = {status: 'resolving'};

/**
 * selectWBAgentBillingAccess derives the gating verdict from billing state,
 * failing closed so any non-ready status is locked. When locked, the reason
 * names the single most actionable cause.
 */
export function selectWBAgentBillingAccess(
  state: WBAgentBillingState
): WBAgentBillingAccess {
  if (state.status === 'unenforced') {
    return {accessible: true};
  }
  if (state.status === 'error') {
    return {accessible: false, reason: 'error'};
  }
  if (state.status === 'resolving') {
    return {accessible: false, reason: 'resolving'};
  }
  // An enforced zero allotment with no pay-as-you-go means the plan includes no
  // agent access at all, so upgrade is the cause even on a trial.
  if (
    state.isQuotaEnforced &&
    !state.isPaygoEnabled &&
    state.quotaLimit === 0
  ) {
    return {accessible: false, reason: 'upgrade'};
  }
  if (state.isTrial && state.isTrialExpired) {
    return {accessible: false, reason: 'trial', expired: true};
  }

  // Pay-as-you-go raises the ceiling to the hard cap but never below the
  // included limit, so the included quota stays available even when a
  // misconfigured safety limit sits under it.
  const threshold = state.isPaygoEnabled
    ? Math.max(state.quotaSafetyLimit, state.quotaLimit)
    : state.quotaLimit;
  const outOfQuota = state.isQuotaEnforced && state.quotaUsed >= threshold;
  if (!outOfQuota) {
    return {accessible: true};
  }

  // With pay-as-you-go the operative ceiling is the hard cap, so a reached cap
  // is the cause even on a trial.
  if (state.isPaygoEnabled) {
    return {accessible: false, reason: 'cap'};
  }
  if (state.isTrial) {
    return {accessible: false, reason: 'trial', expired: false};
  }

  // An exhausted included allotment with paygo off turns the service off until
  // reset.
  return {accessible: false, reason: 'quota'};
}
